// Response rooms: where an incident, a business continuity invocation or an exercise is run while
// it is live. The list (/resilience/rooms) opens rooms and hands out the offline crisis pack; the
// room (/resilience/rooms/:id) holds the timeline, the response roles, the action board, the
// processes that are down against their recovery objectives, the statutory clocks and the
// situation reports. Every rule and every label comes from the API (backend/src/lib/responseRoom.ts
// via GET /response-rooms/:id `guide`); the page only renders it. Writes need resilience:write;
// a response role is filing, never access. Contract: docs/OPERATIONAL-RESILIENCE.md "Response rooms".

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Download, FileText, LifeBuoy, Plus, Radio, RotateCcw, Siren, Users } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../../theme";
import { api, apiBlob, apiErrorMessage, ApiError } from "../../api/client";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { PortalLink } from "../../components/PortalLink";
import { KIND_HREFS } from "../../components/EntityTags";
import { LoadingBlock } from "../../components/Spinner";
import { TierLockModal, TIER_NAMES, type TierErrorPayload } from "../../components/TierLockModal";
import { usePortalNavigate } from "../../hooks/usePortalNavigate";
import { saveBlob, fileNameSafe } from "../../lib/download";
import {
  fmtDateTime, relativeDue, fromInput, nowLocalInput, StatusPill, SeverityPill, Field, input, textarea, secondaryBtn, dangerBtn,
  ModalOverlay, ModalHeader, EmptyState, Check,
} from "./shared";

// ── Types from GET /response-rooms and /response-rooms/:id ───────────────────

type RoomMode = "incident" | "continuity" | "exercise";

interface Guide {
  modes: Array<{ id: RoomMode; label: string; hint: string }>;
  roles: Array<{ id: string; label: string; duty: string }>;
  actionStatuses: Array<{ id: string; label: string }>;
  actionPriorities: string[];
  logKinds: Array<{ id: string; label: string; hint: string }>;
  sitrepIntervals: number[];
  recoveryStates: Record<string, string>;
  crisisPackStaleDays: number;
}

interface RoomListItem {
  id: string; ref: string; mode: RoomMode; title: string; status: string; openedAt: string; stoodDownAt: string | null;
  incident: { id: string; ref: string; title: string; severity: string; status: string } | null;
  exercise: { id: string; title: string } | null;
  leads: string[]; openActions: number; overdueActions: number; processesDown: number;
  lastSitrepAt: string | null; nextSitrepDueAt: string | null;
}

interface RoomList { rooms: RoomListItem[]; crisisPack: { lastDownloadedAt: string | null; staleDays: number }; guide: Guide }

interface Role { id: string; role: string; userId: string | null; name: string; contact: string | null }
interface RoomAction {
  id: string; number: number; title: string; detail: string | null; priority: string; status: string; ownerUserId: string | null; ownerName: string | null;
  dueAt: string | null; outcome: string | null; completedAt: string | null; overdue: boolean;
  correctiveAction: { id: string; displayId: number | null; title: string; status: string } | null;
}
interface Sitrep { id: string; number: number; issuedAt: string; authorName: string | null; situation: string; priorities: string | null; postedToTeamsAt: string | null }
interface Process {
  id: string; biaId: string | null; name: string; rtoHours: number | null; mtpdHours: number | null; disruptedAt: string; workaroundAt: string | null; recoveredAt: string | null; note: string | null;
  bia: { id: string; displayId: number | null; name: string } | null;
  standing: { state: string; hoursDown: number; rtoDueAt: string | null; mtpdDueAt: string | null; metRto: boolean | null; metMtpd: boolean | null; workaround: boolean };
}
interface Notification { id: string; body: string; bodyKey: string; required: boolean; status: string; sentAt: string | null; reference: string | null; dueAt: string | null }
interface TimelineItem { id: string; at: string; source: "milestone" | "log" | "notification" | "room"; kind: string; title: string; text: string | null; author: string | null }

interface RoomView {
  room: {
    id: string; ref: string; mode: RoomMode; title: string; status: string; objective: string | null; sitrepIntervalMinutes: number | null; teamsUpdates: boolean;
    openedAt: string; openedByName: string | null; stoodDownAt: string | null; stoodDownByName: string | null; standDownNote: string | null; evidenceDocumentId: string | null;
  };
  incident: { id: string; ref: string; title: string; severity: string; status: string; awareAt: string; containedAt: string | null; resolvedAt: string | null; notifications: Notification[] } | null;
  exercise: { id: string; title: string; kind: string; status: string } | null;
  roles: Role[];
  actions: RoomAction[];
  sitreps: Sitrep[];
  nextSitrepDueAt: string | null;
  processes: Process[];
  impact: { businessServices: Array<{ id: string; name: string; criticality: string; depth: number; pathText: string }>; truncated: boolean };
  suggestedProcesses: Array<{ id: string; ref: string; name: string }>;
  availableProcesses: Array<{ id: string; ref: string; name: string; status: string; rtoHours: number | null; mtpdHours: number | null }>;
  entries: Array<{ id: string; at: string; kind: string; text: string; authorName: string | null }>;
  timeline: TimelineItem[];
  attention: Array<{ severity: "high" | "medium" | "low"; key: string; message: string }>;
  members: Array<{ id: string; name: string }>;
  guide: Guide;
}

const EXERCISE_BANNER = "Exercise: not a real incident. No regulator clock runs in this room.";
const h3: React.CSSProperties = { ...text.sectionTitle, margin: 0 };

function intervalLabel(min: number): string {
  return min < 60 ? `Every ${min} minutes` : min === 60 ? "Every hour" : min % 1440 === 0 ? (min === 1440 ? "Daily" : `Every ${min / 1440} days`) : `Every ${min / 60} hours`;
}

function hoursText(h: number): string {
  if (h < 1) return `${Math.max(0, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h * 10) / 10} h`;
  return `${Math.round((h / 24) * 10) / 10} days`;
}

const elapsedSince = (iso: string, until?: string | null) => hoursText(((until ? new Date(until) : new Date()).getTime() - new Date(iso).getTime()) / 3_600_000);

function useTierLock() {
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const navigate = usePortalNavigate();
  const modal = tierError ? (
    <TierLockModal
      featureName="Response rooms"
      requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
      requiredTierLevel={tierError.requiredLevel}
      currentTierLevel={tierError.currentLevel}
      onClose={() => { setTierError(null); navigate("/resilience/incidents"); }}
      onRequestUpgrade={() => navigate("/my-services")}
    />
  ) : null;
  const catchTier = (e: unknown) => {
    if (e instanceof ApiError && e.status === 402 && e.code === "tier_locked") { setTierError(e.payload as TierErrorPayload); return true; }
    return false;
  };
  return { modal, catchTier, locked: !!tierError };
}

function ModePill({ mode }: { mode: RoomMode }) {
  const style: Record<RoomMode, { bg: string; color: string; label: string }> = {
    incident:   { bg: theme.color.dangerBg,  color: theme.color.dangerText,  label: "Incident" },
    continuity: { bg: theme.color.warningBg, color: theme.color.warningText, label: "Continuity" },
    exercise:   { bg: theme.color.accentBg,  color: theme.color.accentText,  label: "Exercise" },
  };
  const s = style[mode];
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{s.label}</span>;
}

function RoomStatusPill({ status }: { status: string }) {
  return status === "active" ? <StatusPill value="open" label="Live" /> : <StatusPill value="closed" label="Stood down" />;
}

// ── List page ────────────────────────────────────────────────────────────────

export function ResponseRoomsPage() {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("resilience:write");
  const [data, setData] = useState<RoomList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const tier = useTierLock();

  const load = useCallback(async () => {
    try { setData(await api.get<RoomList>("/response-rooms")); setError(null); }
    catch (e) { if (!tier.catchTier(e)) setError(apiErrorMessage(e, "Could not load the response rooms")); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Arriving from an incident or an exercise ("Open a response room") opens the form pre-filled.
  const preset = useMemo(() => ({ incidentId: params.get("incident"), exerciseId: params.get("exercise"), mode: params.get("mode") as RoomMode | null }), [params]);
  useEffect(() => { if (canEdit && (preset.incidentId || preset.exerciseId || preset.mode)) setOpening(true); }, [canEdit, preset]);

  async function downloadPack() {
    setBusy(true); setError(null);
    try {
      saveBlob(await apiBlob("/response-rooms/crisis-pack"), `${fileNameSafe(`Crisis pack ${new Date().toISOString().slice(0, 10)}`)}.docx`);
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not prepare the crisis pack")); }
    finally { setBusy(false); }
  }

  const live = data?.rooms.filter((r) => r.status === "active") ?? [];
  const past = data?.rooms.filter((r) => r.status !== "active") ?? [];
  const packAge = data?.crisisPack.lastDownloadedAt ? (Date.now() - new Date(data.crisisPack.lastDownloadedAt).getTime()) / 86_400_000 : null;
  const packStale = packAge === null || packAge > (data?.crisisPack.staleDays ?? 90);

  return (
    <div className="page-pad">
      {tier.modal}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><Siren size={20} /> Response rooms</h1>
          <p style={{ ...pageSubtitle, maxWidth: 760 }}>
            Run an incident, a business continuity invocation or an exercise while it is happening: who is doing what, the decisions as they are made, the clocks, the processes that are down and the situation reports. When it is over the room becomes the record.
          </p>
        </div>
        {canEdit && !tier.locked && <button type="button" style={btnPrimary} onClick={() => setOpening(true)}><Plus size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Open a room</button>}
      </div>

      {error && <div style={{ ...card, ...text.body, padding: 14, marginBottom: 16, color: theme.color.dangerText }}>{error}</div>}
      {!data && !error && !tier.locked && <LoadingBlock />}

      {data && (
        <>
          <section style={{ ...card, padding: 16, marginBottom: 20, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", borderColor: packStale ? "var(--c-warning)" : undefined }}>
            <FileText size={22} style={{ color: theme.color.accentText, flexShrink: 0 }} />
            <div style={{ flex: "1 1 320px", minWidth: 0 }}>
              <div style={text.itemTitle}>Offline crisis pack</div>
              <div style={{ ...text.hint, lineHeight: 1.5 }}>
                For when the platform, or your own systems, are the casualty: the first hour, the response roles, who to call, every regulator that could need telling and how fast, and what each business process can tolerate. Keep a copy somewhere that does not depend on your network.
                {" "}{data.crisisPack.lastDownloadedAt
                  ? <strong style={{ color: packStale ? theme.color.warningText : theme.color.text }}>Last taken {fmtDateTime(data.crisisPack.lastDownloadedAt)}{packStale ? `, more than ${data.crisisPack.staleDays} days ago: take a fresh copy.` : "."}</strong>
                  : <strong style={{ color: theme.color.warningText }}>Nobody has taken a copy yet.</strong>}
              </div>
            </div>
            <button type="button" style={secondaryBtn} onClick={downloadPack} disabled={busy}><Download size={12} style={{ verticalAlign: -2, marginRight: 6 }} />{busy ? "Preparing..." : "Download the crisis pack"}</button>
          </section>

          <h2 style={{ ...text.sectionTitle, margin: "0 0 10px" }}>Live</h2>
          {live.length === 0 ? (
            <div style={{ ...card, marginBottom: 20 }}>
              <EmptyState
                title="No room is open"
                detail="Open one as soon as an incident needs more than one person, a business process goes down, or an exercise starts. A room runs a recorded incident, so log the incident first if it is a real one."
                action={canEdit ? <button type="button" style={btnPrimary} onClick={() => setOpening(true)}>Open a room</button> : undefined}
              />
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12, marginBottom: 20 }}>
              {live.map((r) => <RoomCard key={r.id} r={r} />)}
            </div>
          )}

          {past.length > 0 && (
            <>
              <h2 style={{ ...text.sectionTitle, margin: "0 0 10px" }}>Stood down</h2>
              <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", ...text.body }}>
                  <thead>
                    <tr style={{ textAlign: "left", fontSize: text.label.fontSize, color: text.label.color }}>
                      <th style={{ padding: "8px 12px" }}>Room</th><th style={{ padding: "8px 12px" }}>Kind</th><th style={{ padding: "8px 12px" }}>Opened</th><th style={{ padding: "8px 12px" }}>Ran for</th>
                    </tr>
                  </thead>
                  <tbody>
                    {past.map((r) => (
                      <tr key={r.id} style={{ borderTop: "1px solid var(--c-line)" }}>
                        <td style={{ padding: "8px 12px" }}><PortalLink to={`/resilience/rooms/${r.id}`} style={{ color: "var(--c-accentText)" }}><span style={{ ...text.ref }}>{r.ref}</span> {r.title}</PortalLink></td>
                        <td style={{ padding: "8px 12px" }}><ModePill mode={r.mode} /></td>
                        <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{fmtDateTime(r.openedAt)}</td>
                        <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{elapsedSince(r.openedAt, r.stoodDownAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {opening && data && (
        <OpenRoomModal
          guide={data.guide}
          preset={preset}
          onClose={() => { setOpening(false); if (preset.incidentId || preset.exerciseId || preset.mode) setParams({}, { replace: true }); }}
        />
      )}
    </div>
  );
}

function RoomCard({ r }: { r: RoomListItem }) {
  const sitrepLate = r.nextSitrepDueAt && new Date(r.nextSitrepDueAt).getTime() < Date.now();
  return (
    <PortalLink to={`/resilience/rooms/${r.id}`} style={{ textDecoration: "none" }}>
      <div style={{ ...card, padding: 14, height: "100%", borderColor: r.mode === "exercise" ? "var(--c-accent)" : "var(--c-danger)", display: "grid", gap: 6 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ ...text.ref, color: theme.color.muted }}>{r.ref}</span><ModePill mode={r.mode} />
          {r.incident && <SeverityPill value={r.incident.severity} />}
        </div>
        <div style={{ ...text.itemTitle, color: theme.color.heading }}>{r.title}</div>
        <div style={text.hint}>Open {elapsedSince(r.openedAt)}{r.leads.length ? ` · led by ${r.leads.join(", ")}` : " · no lead named"}</div>
        <div style={{ ...text.hint, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span>{r.openActions} open action{r.openActions === 1 ? "" : "s"}{r.overdueActions ? <strong style={{ color: theme.color.dangerText }}> ({r.overdueActions} past due)</strong> : null}</span>
          {r.processesDown > 0 && <span style={{ color: theme.color.warningText }}>{r.processesDown} process{r.processesDown === 1 ? "" : "es"} down</span>}
          {sitrepLate && <span style={{ color: theme.color.warningText }}>Situation report due</span>}
        </div>
      </div>
    </PortalLink>
  );
}

interface OptionRow { id: string; label: string }

function OpenRoomModal({ guide, preset, onClose }: { guide: Guide; preset: { incidentId: string | null; exerciseId: string | null; mode: RoomMode | null }; onClose: () => void }) {
  const navigate = usePortalNavigate();
  const [mode, setMode] = useState<RoomMode>(preset.mode ?? (preset.exerciseId ? "exercise" : "incident"));
  const [incidents, setIncidents] = useState<OptionRow[]>([]);
  const [exercises, setExercises] = useState<OptionRow[]>([]);
  const [incidentId, setIncidentId] = useState(preset.incidentId ?? "");
  const [exerciseId, setExerciseId] = useState(preset.exerciseId ?? "");
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [interval, setInterval] = useState<string>("120");
  const [teams, setTeams] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.get<{ incidents: Array<{ id: string; displayId: number | null; title: string; status: string }> }>("/resilience/incidents")
      .then((d) => { if (live) setIncidents(d.incidents.filter((i) => i.status !== "closed").map((i) => ({ id: i.id, label: `${i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")} ` : ""}${i.title}` }))); })
      .catch(() => { /* the picker stays empty; the server still checks */ });
    api.get<{ exercises: Array<{ id: string; title: string; status: string }> }>("/resilience/exercises")
      .then((d) => { if (live) setExercises(d.exercises.filter((e) => e.status !== "cancelled").map((e) => ({ id: e.id, label: e.title }))); })
      .catch(() => { /* as above */ });
    return () => { live = false; };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const res = await api.post<RoomView>("/response-rooms", {
        mode, title: title.trim() || null, objective: objective.trim() || null,
        incidentId: mode === "exercise" ? null : incidentId || null,
        exerciseId: mode === "exercise" ? exerciseId || null : null,
        sitrepIntervalMinutes: interval ? Number(interval) : null, teamsUpdates: teams,
      });
      navigate(`/resilience/rooms/${res.room.id}`);
    } catch (err) {
      // One room per incident or exercise: go to the one already open.
      if (err instanceof ApiError && err.status === 409 && typeof (err.payload as { roomId?: unknown })?.roomId === "string") {
        navigate(`/resilience/rooms/${(err.payload as { roomId: string }).roomId}`);
        return;
      }
      setError(apiErrorMessage(err, "Could not open the room"));
    } finally { setBusy(false); }
  }

  const modeHint = guide.modes.find((m) => m.id === mode)?.hint;
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Open a response room" onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="What is the room for" span2 help={modeHint}>
          <select value={mode} onChange={(e) => setMode(e.target.value as RoomMode)} style={input}>
            {guide.modes.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </Field>
        {mode !== "exercise" && (
          <Field label={mode === "incident" ? "The incident" : "The incident behind it (optional)"} span2 help={mode === "incident" ? "A room runs a recorded incident, so its notification clocks come with it. Log the incident first if it is not on the register." : undefined}>
            <select value={incidentId} onChange={(e) => setIncidentId(e.target.value)} style={input} required={mode === "incident"}>
              <option value="">{mode === "incident" ? "Pick an open incident" : "No incident: a power cut, a flood, a supplier failure"}</option>
              {incidents.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
            </select>
          </Field>
        )}
        {mode === "exercise" && (
          <Field label="The exercise (optional)" span2>
            <select value={exerciseId} onChange={(e) => setExerciseId(e.target.value)} style={input}>
              <option value="">A drill not on the exercise register</option>
              {exercises.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </Field>
        )}
        <Field label="Title" span2 help={incidentId || exerciseId ? "Leave empty to use the incident's or exercise's own title." : undefined}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={200} required={!incidentId && !exerciseId} />
        </Field>
        <Field label="Objective" span2 help="What the response is trying to achieve, and what standing down looks like.">
          <textarea rows={2} value={objective} onChange={(e) => setObjective(e.target.value)} style={textarea} maxLength={4000} />
        </Field>
        <Field label="Situation reports">
          <select value={interval} onChange={(e) => setInterval(e.target.value)} style={input}>
            <option value="">When the lead calls one</option>
            {guide.sitrepIntervals.map((m) => <option key={m} value={String(m)}>{intervalLabel(m)}</option>)}
          </select>
        </Field>
        <div style={{ alignSelf: "end" }}>
          <Check checked={teams} onChange={setTeams} label="Post situation reports to Teams" help="Uses the organisation's Teams channel, when one is set up." />
        </div>
        {error && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
          <button type="submit" disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>{busy ? "Opening..." : "Open the room"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Room page ────────────────────────────────────────────────────────────────

const POLL_MS = 15_000;

export function ResponseRoomPage() {
  const { roomId = "" } = useParams<{ roomId: string }>();
  const { user } = useCurrentUser();
  const [view, setView] = useState<RoomView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<null | "sitrep" | "standdown" | "reopen" | "settings" | "roles">(null);
  const [busy, setBusy] = useState<string | null>(null);
  const tier = useTierLock();
  const canEdit = !!user?.permissions?.includes("resilience:write") && view?.room.status === "active";
  const canManage = !!user?.permissions?.includes("resilience:write");

  const load = useCallback(async () => {
    try { setView(await api.get<RoomView>(`/response-rooms/${roomId}`)); setError(null); }
    catch (e) { if (!tier.catchTier(e)) setError(apiErrorMessage(e, "Could not load the room")); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);
  useEffect(() => { void load(); }, [load]);

  // A live room is shared: refresh while the tab is visible so everyone sees the same board.
  const active = view?.room.status === "active";
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [active, load]);

  /** Every write answers with the whole room, so one helper applies it. */
  const write = useCallback(async (key: string, fn: () => Promise<RoomView>): Promise<boolean> => {
    setBusy(key); setError(null);
    try { setView(await fn()); return true; }
    catch (e) { setError(apiErrorMessage(e, "That did not save")); return false; }
    finally { setBusy(null); }
  }, []);

  async function download(path: string, name: string) {
    setBusy("download"); setError(null);
    try { saveBlob(await apiBlob(path), `${fileNameSafe(name)}.docx`); }
    catch (e) { setError(apiErrorMessage(e, "Could not prepare the document")); }
    finally { setBusy(null); }
  }

  if (!view) {
    return <div className="page-pad">{tier.modal}{error ? <div style={{ ...card, ...text.body, padding: 14, color: theme.color.dangerText }}>{error}</div> : <LoadingBlock />}</div>;
  }
  const { room } = view;
  const exercise = room.mode === "exercise";
  const sitrepLate = view.nextSitrepDueAt && new Date(view.nextSitrepDueAt).getTime() < Date.now();

  return (
    <div className="page-pad">
      {tier.modal}
      <div style={{ marginBottom: 8 }}><PortalLink to="/resilience/rooms" style={{ ...text.hint, color: "var(--c-accentText)" }}>All response rooms</PortalLink></div>
      {exercise && (
        <div role="note" style={{ padding: "8px 12px", marginBottom: 12, borderRadius: 3, background: "var(--c-accentBg)", border: "1px solid var(--c-accent)", ...text.body, fontWeight: 600, color: "var(--c-accentText)" }}>{EXERCISE_BANNER}</div>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ ...text.ref, color: theme.color.muted }}>{room.ref}</span><ModePill mode={room.mode} /><RoomStatusPill status={room.status} />
            {view.incident && <SeverityPill value={view.incident.severity} />}
          </div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><LifeBuoy size={20} /> {room.title}</h1>
          <p style={{ ...pageSubtitle, margin: 0 }}>
            Opened {fmtDateTime(room.openedAt)}{room.openedByName ? ` by ${room.openedByName}` : ""} · {room.stoodDownAt ? `stood down ${fmtDateTime(room.stoodDownAt)} after ${elapsedSince(room.openedAt, room.stoodDownAt)}` : `running ${elapsedSince(room.openedAt)}`}
            {view.incident && <> · <PortalLink to={`/resilience/incidents?incident=${view.incident.id}`} style={{ color: "var(--c-accentText)" }}>{view.incident.ref} on the incident register</PortalLink></>}
            {view.exercise && <> · <PortalLink to={`/resilience/exercises?exercise=${view.exercise.id}`} style={{ color: "var(--c-accentText)" }}>the exercise record</PortalLink></>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canEdit && <button type="button" style={btnPrimary} onClick={() => setModal("sitrep")}><Radio size={12} style={{ verticalAlign: -2, marginRight: 6 }} />Issue a situation report</button>}
          <button type="button" style={secondaryBtn} disabled={busy === "download"} onClick={() => download(`/response-rooms/${room.id}/record`, `${room.ref} ${room.title}`)}><Download size={12} style={{ verticalAlign: -2, marginRight: 6 }} />Room record</button>
          {canEdit && <button type="button" style={secondaryBtn} onClick={() => setModal("settings")}>Settings</button>}
          {canEdit && <button type="button" style={dangerBtn} onClick={() => setModal("standdown")}>Stand down</button>}
          {canManage && room.status !== "active" && <button type="button" style={secondaryBtn} onClick={() => setModal("reopen")}><RotateCcw size={12} style={{ verticalAlign: -2, marginRight: 6 }} />Reopen</button>}
        </div>
      </div>

      {error && <div style={{ ...card, ...text.body, padding: 12, marginBottom: 12, color: theme.color.dangerText }}>{error}</div>}

      {room.status !== "active" && room.standDownNote && (
        <div style={{ ...card, padding: 14, marginBottom: 16 }}>
          <div style={text.label}>Stand-down</div>
          <div style={{ ...text.body, whiteSpace: "pre-wrap" }}>{room.standDownNote}</div>
          <div style={{ ...text.hint, marginTop: 6 }}>The room is the record of the response now{room.evidenceDocumentId ? " and is in the Evidence Repository" : ""}. Reopen it to carry on.</div>
        </div>
      )}

      {view.attention.length > 0 && (
        <section style={{ ...card, padding: 12, marginBottom: 16, borderColor: view.attention.some((a) => a.severity === "high") ? "var(--c-danger)" : "var(--c-warning)" }}>
          <div style={{ display: "grid", gap: 4 }}>
            {view.attention.map((a) => (
              <div key={a.key} style={{ ...text.body, display: "flex", gap: 8, alignItems: "baseline" }}>
                <AlertTriangle size={13} style={{ flexShrink: 0, color: a.severity === "high" ? theme.color.dangerText : a.severity === "medium" ? theme.color.warningText : theme.color.muted, position: "relative", top: 2 }} />
                <span style={{ color: a.severity === "high" ? theme.color.dangerText : theme.color.text, fontWeight: a.severity === "high" ? 600 : 400 }}>{a.message}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {room.objective && (
        <div style={{ ...text.body, marginBottom: 16 }}><span style={{ ...text.label, marginRight: 6 }}>Objective</span><span style={{ whiteSpace: "pre-wrap" }}>{room.objective}</span></div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", gap: 16, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 16, minWidth: 0 }}>
          <ActionBoard view={view} canEdit={canEdit} busy={busy} write={write} />
          <Timeline view={view} canEdit={canEdit} busy={busy} write={write} />
        </div>
        <div style={{ display: "grid", gap: 16, minWidth: 0 }}>
          {view.incident && !exercise && <Clocks view={view} />}
          <Team view={view} canEdit={canEdit} onEdit={() => setModal("roles")} />
          <Processes view={view} canEdit={canEdit} busy={busy} write={write} />
          <Sitreps view={view} sitrepLate={!!sitrepLate} onDownload={(s) => download(`/response-rooms/${room.id}/sitreps/${s.id}/document`, `${room.ref} situation report ${s.number}`)} />
        </div>
      </div>

      {modal === "sitrep" && <SitrepModal view={view} onClose={() => setModal(null)} write={write} />}
      {modal === "standdown" && <StandDownModal view={view} onClose={() => setModal(null)} write={write} />}
      {modal === "reopen" && <ReopenModal view={view} onClose={() => setModal(null)} write={write} />}
      {modal === "settings" && <SettingsModal view={view} onClose={() => setModal(null)} write={write} />}
      {modal === "roles" && <RolesModal view={view} onClose={() => setModal(null)} write={write} />}
    </div>
  );
}

type Write = (key: string, fn: () => Promise<RoomView>) => Promise<boolean>;

function Section({ title, icon, aside, children }: { title: string; icon?: React.ReactNode; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        {icon}<h3 style={h3}>{title}</h3><span style={{ flex: 1 }} />{aside}
      </div>
      {children}
    </section>
  );
}

// ── Clocks ───────────────────────────────────────────────────────────────────

function Clocks({ view }: { view: RoomView }) {
  const inc = view.incident!;
  const required = inc.notifications.filter((n) => n.required);
  return (
    <Section title="Who must be told" aside={<span style={text.hint}>Aware {fmtDateTime(inc.awareAt)}</span>}>
      {required.length === 0 ? <div style={{ ...text.body, color: theme.color.muted }}>No notification duty is derived for this incident.</div> : (
        <div style={{ display: "grid", gap: 6 }}>
          {required.map((n) => {
            const late = n.status === "pending" && n.dueAt && new Date(n.dueAt).getTime() < Date.now();
            return (
              <div key={n.id} style={{ ...text.body, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <StatusPill value={late ? "overdue" : n.status} />
                <span style={{ fontWeight: 600 }}>{n.body}</span>
                <span style={{ ...text.hint, color: late ? theme.color.dangerText : theme.color.muted }}>{n.status === "pending" ? relativeDue(n.dueAt) : n.sentAt ? `sent ${fmtDateTime(n.sentAt)}` : ""}</span>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ ...text.hint, marginTop: 8 }}>Record a notification as sent on <PortalLink to={`/resilience/incidents?incident=${inc.id}`} style={{ color: "var(--c-accentText)" }}>the incident</PortalLink>: that is the regulatory record.</div>
    </Section>
  );
}

// ── Team ─────────────────────────────────────────────────────────────────────

function Team({ view, canEdit, onEdit }: { view: RoomView; canEdit: boolean; onEdit: () => void }) {
  return (
    <Section title="Response team" icon={<Users size={15} />} aside={canEdit ? <button type="button" style={secondaryBtn} onClick={onEdit}>Assign roles</button> : undefined}>
      <div style={{ display: "grid", gap: 6 }}>
        {view.guide.roles.map((r) => {
          const holders = view.roles.filter((x) => x.role === r.id);
          return (
            <div key={r.id} style={{ ...text.body, display: "grid", gridTemplateColumns: "minmax(120px, 190px) 1fr", gap: 8 }} title={r.duty}>
              <span style={{ ...text.label, marginBottom: 0 }}>{r.label}</span>
              <span style={{ color: holders.length ? theme.color.text : theme.color.faint }}>
                {holders.length ? holders.map((h) => `${h.name}${h.contact ? ` (${h.contact})` : ""}`).join(", ") : "Not assigned"}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ ...text.hint, marginTop: 8 }}>Roles say who does what. They do not change what anyone can see or edit.</div>
    </Section>
  );
}

function RolesModal({ view, onClose, write }: { view: RoomView; onClose: () => void; write: Write }) {
  const [rows, setRows] = useState(view.roles.map((r) => ({ role: r.role, userId: r.userId ?? "", name: r.userId ? "" : r.name, contact: r.contact ?? "" })));
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const ok = await write("roles", () => api.put<RoomView>(`/response-rooms/${view.room.id}/roles`, {
      roles: rows.filter((r) => r.userId || r.name.trim()).map((r) => ({ role: r.role, userId: r.userId || null, name: r.name.trim() || null, contact: r.contact.trim() || null })),
    }));
    if (ok) onClose();
  }
  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title="Response roles" onClose={onClose} />
      <form onSubmit={save} style={{ padding: 20, display: "grid", gap: 10 }}>
        <div style={text.hint}>Pick a member, or type the name of someone outside the platform (a retained responder, the insurer's panel firm). Add a phone number for when email is down.</div>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr)) auto", gap: 8, alignItems: "end" }}>
            <Field label="Role">
              <select value={r.role} onChange={(e) => set(i, { role: e.target.value })} style={input}>
                {view.guide.roles.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </Field>
            <Field label="Member">
              <select value={r.userId} onChange={(e) => set(i, { userId: e.target.value })} style={input}>
                <option value="">Someone else</option>
                {view.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
            {!r.userId && <Field label="Name"><input value={r.name} onChange={(e) => set(i, { name: e.target.value })} style={input} maxLength={200} /></Field>}
            <Field label="Phone or other route"><input value={r.contact} onChange={(e) => set(i, { contact: e.target.value })} style={input} maxLength={200} /></Field>
            <button type="button" style={secondaryBtn} onClick={() => setRows((list) => list.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <div><button type="button" style={secondaryBtn} onClick={() => setRows((list) => [...list, { role: view.guide.roles.find((g) => !list.some((x) => x.role === g.id))?.id ?? "technical", userId: "", name: "", contact: "" }])}>Add a role holder</button></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" style={btnPrimary}>Save roles</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Action board ─────────────────────────────────────────────────────────────

const ACTION_TONE: Record<string, string> = { open: "planned", in_progress: "in_progress", blocked: "blocked", done: "completed", dropped: "cancelled" };

function ActionBoard({ view, canEdit, busy, write }: { view: RoomView; canEdit: boolean; busy: string | null; write: Write }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("medium");
  const [reasonFor, setReasonFor] = useState<{ action: RoomAction; to: string } | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const label = (s: string) => view.guide.actionStatuses.find((x) => x.id === s)?.label ?? s;
  const open = view.actions.filter((a) => a.status !== "done" && a.status !== "dropped");
  const closed = view.actions.filter((a) => a.status === "done" || a.status === "dropped");

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const ok = await write("action:new", () => api.post<RoomView>(`/response-rooms/${view.room.id}/actions`, { title: title.trim(), ownerUserId: owner || null, dueAt: fromInput(due), priority }));
    if (ok) { setTitle(""); setOwner(""); setDue(""); setPriority("medium"); setAdding(false); }
  }
  const move = (a: RoomAction, status: string, outcome?: string) =>
    write(`action:${a.id}`, () => api.patch<RoomView>(`/response-rooms/${view.room.id}/actions/${a.id}`, { status, ...(outcome !== undefined ? { outcome } : {}) }));

  const row = (a: RoomAction) => (
    <div key={a.id} style={{ padding: "8px 10px", borderRadius: 3, border: `1px solid ${a.overdue ? "var(--c-danger)" : "var(--c-line)"}`, display: "grid", gap: 4 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ ...text.ref, color: theme.color.muted }}>A-{a.number}</span>
        <span style={{ ...text.body, fontWeight: 600, flex: "1 1 160px", minWidth: 0 }}>{a.title}</span>
        {a.priority === "high" && <SeverityPill value="high" />}
        <StatusPill value={ACTION_TONE[a.status] ?? a.status} label={label(a.status)} />
      </div>
      <div style={{ ...text.hint, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <span>{a.ownerName ?? <em>No owner</em>}</span>
        {a.dueAt && <span style={{ color: a.overdue ? theme.color.dangerText : undefined }}>{a.status === "done" || a.status === "dropped" ? `due ${fmtDateTime(a.dueAt)}` : relativeDue(a.dueAt)}</span>}
        {a.outcome && <span style={{ whiteSpace: "pre-wrap" }}>{a.outcome}</span>}
        {a.correctiveAction && <PortalLink to={KIND_HREFS.action(a.correctiveAction.id) ?? "/improvement"} style={{ color: "var(--c-accentText)" }}>Carried to CA-{String(a.correctiveAction.displayId ?? "").padStart(3, "0")}</PortalLink>}
      </div>
      {canEdit && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {a.status === "open" && <button type="button" style={secondaryBtn} disabled={busy === `action:${a.id}`} onClick={() => move(a, "in_progress")}>Start</button>}
          {(a.status === "open" || a.status === "in_progress" || a.status === "blocked") && <button type="button" style={secondaryBtn} disabled={busy === `action:${a.id}`} onClick={() => move(a, "done")}>Done</button>}
          {a.status === "blocked" && <button type="button" style={secondaryBtn} disabled={busy === `action:${a.id}`} onClick={() => move(a, "in_progress")}>Unblock</button>}
          {(a.status === "open" || a.status === "in_progress") && <button type="button" style={secondaryBtn} onClick={() => setReasonFor({ action: a, to: "blocked" })}>Blocked</button>}
          {(a.status === "open" || a.status === "in_progress" || a.status === "blocked") && <button type="button" style={secondaryBtn} onClick={() => setReasonFor({ action: a, to: "dropped" })}>Drop</button>}
          {(a.status === "done" || a.status === "dropped") && !a.correctiveAction && <button type="button" style={secondaryBtn} disabled={busy === `action:${a.id}`} onClick={() => move(a, "open")}>Reopen</button>}
        </div>
      )}
    </div>
  );

  return (
    <Section title="Actions" aside={canEdit && !adding ? <button type="button" style={secondaryBtn} onClick={() => setAdding(true)}><Plus size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Add an action</button> : undefined}>
      {adding && (
        <form onSubmit={add} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, alignItems: "end", marginBottom: 12 }}>
          <div style={{ gridColumn: "1 / -1" }}><Field label="What needs doing"><input value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={300} required autoFocus /></Field></div>
          <Field label="Owner">
            <select value={owner} onChange={(e) => setOwner(e.target.value)} style={input}>
              <option value="">Nobody yet</option>
              {view.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="By"><input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} style={input} /></Field>
          <Field label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={input}>
              {view.guide.actionPriorities.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </Field>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="submit" style={btnPrimary} disabled={busy === "action:new"}>Add</button>
            <button type="button" style={secondaryBtn} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}
      {open.length === 0 && !adding && <div style={{ ...text.body, color: theme.color.muted }}>No open actions.</div>}
      <div style={{ display: "grid", gap: 6 }}>{open.map(row)}</div>
      {closed.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setShowClosed((s) => !s)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", ...text.hint, color: "var(--c-accentText)", fontWeight: 600, fontFamily: theme.font }}>
            {showClosed ? "Hide" : "Show"} {closed.length} closed action{closed.length === 1 ? "" : "s"}
          </button>
          {showClosed && <div style={{ display: "grid", gap: 6, marginTop: 6 }}>{closed.map(row)}</div>}
        </div>
      )}
      {reasonFor && (
        <ReasonModal
          title={reasonFor.to === "blocked" ? `What is A-${reasonFor.action.number} blocked on?` : `Why is A-${reasonFor.action.number} no longer needed?`}
          onClose={() => setReasonFor(null)}
          onSave={async (reason) => { if (await move(reasonFor.action, reasonFor.to, reason)) setReasonFor(null); }}
        />
      )}
    </Section>
  );
}

function ReasonModal({ title, onClose, onSave }: { title: string; onClose: () => void; onSave: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState("");
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <form onSubmit={(e) => { e.preventDefault(); if (reason.trim()) void onSave(reason.trim()); }} style={{ padding: 20, display: "grid", gap: 12 }}>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} style={textarea} maxLength={4000} autoFocus required />
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" style={btnPrimary} disabled={!reason.trim()}>Save</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Timeline ─────────────────────────────────────────────────────────────────

const DOT: Record<TimelineItem["source"], string> = {
  milestone: theme.color.accent,
  notification: theme.color.success,
  log: theme.color.borderStrong,
  room: theme.color.warning,
};

function Timeline({ view, canEdit, busy, write }: { view: RoomView; canEdit: boolean; busy: string | null; write: Write }) {
  const [kind, setKind] = useState("observation");
  const [at, setAt] = useState(nowLocalInput());
  const [note, setNote] = useState("");
  const [corrects, setCorrects] = useState<RoomView["entries"][number] | null>(null);
  const [newestFirst, setNewestFirst] = useState(true);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const entryById = new Map(view.entries.map((e) => [e.id, e]));

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    const ok = await write("log", () => api.post<RoomView>(`/response-rooms/${view.room.id}/log`, { kind, at: fromInput(at), text: note.trim(), correctsEntryId: corrects?.id ?? null }));
    if (ok) { setNote(""); setCorrects(null); setAt(nowLocalInput()); }
  }

  const items = newestFirst ? [...view.timeline].reverse() : view.timeline;
  return (
    <Section title="Log and timeline" aside={
      <button type="button" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", ...text.hint, color: "var(--c-accentText)", fontWeight: 600, fontFamily: theme.font }} onClick={() => setNewestFirst((v) => !v)}>
        {newestFirst ? "Newest first" : "Oldest first"}
      </button>
    }>
      {canEdit && (
        <form onSubmit={add} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, alignItems: "end", marginBottom: 12 }}>
          <Field label="Kind">
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={input} title={view.guide.logKinds.find((k) => k.id === kind)?.hint}>
              {view.guide.logKinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </Field>
          <Field label="When"><input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} style={input} required /></Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label={corrects ? `Correction to the entry of ${fmtDateTime(corrects.at)}` : "Entry"}>
              <textarea ref={noteRef} rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} maxLength={5000}
                placeholder={view.guide.logKinds.find((k) => k.id === kind)?.hint}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void add(e as unknown as React.FormEvent); } }} />
            </Field>
          </div>
          <div style={{ display: "flex", gap: 6, gridColumn: "1 / -1", alignItems: "center", flexWrap: "wrap" }}>
            <button type="submit" disabled={busy === "log" || !note.trim()} style={{ ...btnPrimary, opacity: busy === "log" || !note.trim() ? 0.6 : 1 }}>{busy === "log" ? "Adding..." : "Add to the log"}</button>
            {corrects && <button type="button" style={secondaryBtn} onClick={() => setCorrects(null)}>Not a correction</button>}
            <span style={text.hint}>Ctrl+Enter adds it. Entries cannot be edited or deleted; correct one with a new entry.</span>
          </div>
        </form>
      )}
      {items.length === 0 ? <div style={{ ...text.body, color: theme.color.muted }}>Nothing recorded yet.</div> : (
        <ol style={{ listStyle: "none", margin: 0, padding: 0, borderLeft: `2px solid ${theme.color.border}`, marginLeft: 6 }}>
          {items.map((t) => {
            const entry = t.source === "log" ? entryById.get(t.id.slice(4)) : undefined;
            return (
              <li key={t.id} style={{ position: "relative", padding: "0 0 12px 16px" }}>
                <span style={{ position: "absolute", left: -6, top: 4, width: 10, height: 10, borderRadius: 999, background: DOT[t.source] }} />
                <div style={text.meta}>{fmtDateTime(t.at)}{t.author ? ` · ${t.author}` : ""}</div>
                <div style={{ ...text.body, lineHeight: 1.45 }}>
                  <strong style={{ fontWeight: t.source === "log" ? 600 : 700, color: t.source === "log" ? theme.color.muted : theme.color.heading }}>{t.title}</strong>
                  {t.text ? <span style={{ whiteSpace: "pre-wrap" }}>{t.source === "log" ? ": " : " · "}{t.text}</span> : null}
                  {entry && canEdit && (
                    <button type="button" onClick={() => { setCorrects(entry); setKind(entry.kind); noteRef.current?.focus(); }} style={{ marginLeft: 8, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--c-accentText)", fontSize: text.meta.fontSize, fontFamily: theme.font }}>Correct</button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Section>
  );
}

// ── Business processes ───────────────────────────────────────────────────────

const RECOVERY_TONE: Record<string, string> = { recovered: "closed", within_rto: "pending", rto_breached: "overdue", mtpd_breached: "overdue", no_objective: "not_required" };

function Processes({ view, canEdit, busy, write }: { view: RoomView; canEdit: boolean; busy: string | null; write: Write }) {
  const [biaId, setBiaId] = useState("");
  const [since, setSince] = useState(nowLocalInput());
  const add = (id: string, disruptedAt: string | null) => write("process:new", () => api.post<RoomView>(`/response-rooms/${view.room.id}/processes`, { biaId: id, disruptedAt }));
  const patch = (p: Process, body: Record<string, unknown>) => write(`process:${p.id}`, () => api.patch<RoomView>(`/response-rooms/${view.room.id}/processes/${p.id}`, body));
  const remove = (p: Process) => write(`process:${p.id}`, () => api.del<RoomView>(`/response-rooms/${view.room.id}/processes/${p.id}`));
  const nothing = view.processes.length === 0 && view.suggestedProcesses.length === 0 && view.impact.businessServices.length === 0;

  return (
    <Section title="Business impact">
      {view.impact.businessServices.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ ...text.label, marginBottom: 4 }}>Business services affected through the asset dependencies</div>
          {view.impact.businessServices.map((b) => (
            <div key={b.id} style={{ ...text.body }}>{b.name}{b.pathText ? <span style={text.hint}> · {b.pathText}</span> : null}</div>
          ))}
        </div>
      )}
      {view.processes.length > 0 && (
        <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
          {view.processes.map((p) => (
            <div key={p.id} style={{ padding: "8px 10px", borderRadius: 3, border: `1px solid ${p.standing.state === "rto_breached" || p.standing.state === "mtpd_breached" ? "var(--c-danger)" : "var(--c-line)"}`, display: "grid", gap: 4 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ ...text.body, fontWeight: 600, flex: 1 }}>{p.name}</span>
                <StatusPill value={RECOVERY_TONE[p.standing.state] ?? "pending"} label={view.guide.recoveryStates[p.standing.state] ?? p.standing.state} />
              </div>
              <div style={{ ...text.hint, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <span>Down {fmtDateTime(p.disruptedAt)} ({hoursText(p.standing.hoursDown)}{p.recoveredAt ? "" : " so far"})</span>
                {p.rtoHours != null && <span>RTO {p.rtoHours} h{!p.recoveredAt && p.standing.rtoDueAt ? ` (${relativeDue(p.standing.rtoDueAt).replace("due in", "in")})` : ""}</span>}
                {p.mtpdHours != null && <span>MTPD {p.mtpdHours} h</span>}
                {p.workaroundAt && !p.recoveredAt && <span>Workaround since {fmtDateTime(p.workaroundAt)}</span>}
                {p.recoveredAt && <span>Recovered {fmtDateTime(p.recoveredAt)}</span>}
              </div>
              {p.recoveredAt && (p.standing.metRto !== null || p.standing.metMtpd !== null) && (
                <div style={{ ...text.hint, display: "flex", gap: 10, flexWrap: "wrap", fontWeight: 600, color: p.standing.metRto === false || p.standing.metMtpd === false ? theme.color.dangerText : theme.color.successText }}>
                  {p.standing.metRto !== null && p.rtoHours != null && <span>{p.standing.metRto ? `Recovery time objective met (${p.rtoHours} h)` : `Recovery time objective missed: back after ${hoursText(p.standing.hoursDown)}, objective ${p.rtoHours} h`}</span>}
                  {p.standing.metMtpd !== null && p.mtpdHours != null && <span>{p.standing.metMtpd ? `Maximum tolerable downtime met (${p.mtpdHours} h)` : `Maximum tolerable downtime exceeded (${p.mtpdHours} h)`}</span>}
                </div>
              )}
              {canEdit && !p.recoveredAt && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {!p.workaroundAt && <button type="button" style={secondaryBtn} disabled={busy === `process:${p.id}`} onClick={() => patch(p, { workaroundAt: new Date().toISOString() })}>Workaround in place</button>}
                  <button type="button" style={secondaryBtn} disabled={busy === `process:${p.id}`} onClick={() => patch(p, { recoveredAt: new Date().toISOString() })}>Recovered now</button>
                  <button type="button" style={secondaryBtn} disabled={busy === `process:${p.id}`} onClick={() => { if (window.confirm(`Take ${p.name} off this room? It will be logged as recorded in error.`)) void remove(p); }}>Added in error</button>
                </div>
              )}
              {canEdit && p.recoveredAt && <div><button type="button" style={secondaryBtn} disabled={busy === `process:${p.id}`} onClick={() => patch(p, { recoveredAt: null })}>Not recovered after all</button></div>}
            </div>
          ))}
        </div>
      )}
      {canEdit && view.suggestedProcesses.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ ...text.label, marginBottom: 4 }}>Probably affected, from the incident's assets</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {view.suggestedProcesses.map((s) => <button key={s.id} type="button" style={secondaryBtn} disabled={busy === "process:new"} onClick={() => add(s.id, null)}>Mark {s.name} as down</button>)}
          </div>
        </div>
      )}
      {canEdit && view.availableProcesses.length > 0 && (
        <form onSubmit={(e) => { e.preventDefault(); if (biaId) void add(biaId, fromInput(since)).then((ok) => { if (ok) setBiaId(""); }); }} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, alignItems: "end" }}>
          <Field label="A process is down">
            <select value={biaId} onChange={(e) => setBiaId(e.target.value)} style={input}>
              <option value="">Pick a business impact analysis</option>
              {view.availableProcesses.map((b) => <option key={b.id} value={b.id}>{b.ref} {b.name}{b.rtoHours != null ? ` (RTO ${b.rtoHours} h)` : ""}</option>)}
            </select>
          </Field>
          <Field label="Since"><input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} style={input} /></Field>
          <div><button type="submit" style={secondaryBtn} disabled={!biaId || busy === "process:new"}>Add</button></div>
        </form>
      )}
      {nothing && (
        <div style={{ ...text.body, color: theme.color.muted }}>
          {view.availableProcesses.length === 0
            ? <>No business impact analyses yet. <PortalLink to="/resilience/bia" style={{ color: "var(--c-accentText)" }}>Analyse your key processes</PortalLink> so a room can track recovery against their objectives.</>
            : "No process is recorded as down."}
        </div>
      )}
    </Section>
  );
}

// ── Situation reports ────────────────────────────────────────────────────────

function Sitreps({ view, sitrepLate, onDownload }: { view: RoomView; sitrepLate: boolean; onDownload: (s: Sitrep) => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <Section title="Situation reports" icon={<Radio size={15} />} aside={
      view.nextSitrepDueAt ? <span style={{ ...text.hint, color: sitrepLate ? theme.color.warningText : undefined }}>Next {relativeDue(view.nextSitrepDueAt)}</span> : undefined
    }>
      {view.sitreps.length === 0 ? <div style={{ ...text.body, color: theme.color.muted }}>None issued yet.</div> : (
        <div style={{ display: "grid", gap: 6 }}>
          {view.sitreps.map((s) => (
            <div key={s.id} style={{ padding: "6px 0", borderTop: "1px solid var(--c-line)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" onClick={() => setOpenId(openId === s.id ? null : s.id)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", ...text.body, fontWeight: 600, color: theme.color.heading, fontFamily: theme.font }}>
                  Report {s.number}
                </button>
                <span style={text.hint}>{fmtDateTime(s.issuedAt)}{s.authorName ? ` · ${s.authorName}` : ""}{s.postedToTeamsAt ? " · posted to Teams" : ""}</span>
                <span style={{ flex: 1 }} />
                <button type="button" style={secondaryBtn} title={`Download report ${s.number} as Word`} aria-label={`Download report ${s.number} as Word`} onClick={() => onDownload(s)}><Download size={12} style={{ verticalAlign: -2, marginRight: 6 }} />Download</button>
              </div>
              {openId === s.id && (
                <div style={{ ...text.body, marginTop: 6, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {s.situation}
                  {s.priorities && <><div style={{ ...text.label, marginTop: 6 }}>Priorities</div>{s.priorities}</>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function SitrepModal({ view, onClose, write }: { view: RoomView; onClose: () => void; write: Write }) {
  const [situation, setSituation] = useState("");
  const [priorities, setPriorities] = useState("");
  const [issuing, setIssuing] = useState(false);
  async function issue(e: React.FormEvent) {
    e.preventDefault();
    if (issuing) return; // a second click must not issue a second report (RES-4)
    setIssuing(true);
    const ok = await write("sitrep", () => api.post<RoomView>(`/response-rooms/${view.room.id}/sitreps`, { situation: situation.trim(), priorities: priorities.trim() || null }));
    if (ok) onClose(); else setIssuing(false);
  }
  const next = (view.sitreps[0]?.number ?? 0) + 1;
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Situation report ${next}`} onClose={onClose} />
      <form onSubmit={issue} style={{ padding: 20, display: "grid", gap: 12 }}>
        <div style={text.hint}>
          Write where things stand and what happens next in your own words. The report adds the rest from the room as it is now: the clocks, the processes down, the actions, the decisions and the log since the last report. It cannot be edited once issued.
          {view.room.teamsUpdates ? " It will be posted to the organisation's Teams channel." : ""}
        </div>
        <Field label="Situation"><textarea rows={5} value={situation} onChange={(e) => setSituation(e.target.value)} style={textarea} maxLength={6000} required autoFocus /></Field>
        <Field label="Priorities for the next period"><textarea rows={3} value={priorities} onChange={(e) => setPriorities(e.target.value)} style={textarea} maxLength={4000} /></Field>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" style={btnPrimary} disabled={!situation.trim() || issuing}>{issuing ? "Issuing..." : `Issue report ${next}`}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Settings, stand-down, reopen ─────────────────────────────────────────────

function SettingsModal({ view, onClose, write }: { view: RoomView; onClose: () => void; write: Write }) {
  const r = view.room;
  const [title, setTitle] = useState(r.title);
  const [objective, setObjective] = useState(r.objective ?? "");
  const [interval, setInterval] = useState(r.sitrepIntervalMinutes ? String(r.sitrepIntervalMinutes) : "");
  const [teams, setTeams] = useState(r.teamsUpdates);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const ok = await write("settings", () => api.patch<RoomView>(`/response-rooms/${r.id}`, { title: title.trim(), objective: objective.trim() || null, sitrepIntervalMinutes: interval ? Number(interval) : null, teamsUpdates: teams }));
    if (ok) onClose();
  }
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Room settings" onClose={onClose} />
      <form onSubmit={save} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Title" span2><input value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={200} required /></Field>
        <Field label="Objective" span2><textarea rows={3} value={objective} onChange={(e) => setObjective(e.target.value)} style={textarea} maxLength={4000} /></Field>
        <Field label="Situation reports">
          <select value={interval} onChange={(e) => setInterval(e.target.value)} style={input}>
            <option value="">When the lead calls one</option>
            {view.guide.sitrepIntervals.map((m) => <option key={m} value={String(m)}>{intervalLabel(m)}</option>)}
          </select>
        </Field>
        <div style={{ alignSelf: "end" }}><Check checked={teams} onChange={setTeams} label="Post situation reports to Teams" /></div>
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
          <button type="submit" style={btnPrimary}>Save</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function StandDownModal({ view, onClose, write }: { view: RoomView; onClose: () => void; write: Write }) {
  const open = view.actions.filter((a) => a.status !== "done" && a.status !== "dropped");
  const [carry, setCarry] = useState<Set<string>>(new Set(open.map((a) => a.id)));
  const [note, setNote] = useState("");
  const left = open.filter((a) => !carry.has(a.id));
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const ok = await write("standdown", () => api.post<RoomView>(`/response-rooms/${view.room.id}/stand-down`, { note: note.trim(), carryForward: [...carry] }));
    if (ok) onClose();
  }
  const down = view.processes.filter((p) => !p.recoveredAt);
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Stand the room down" onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 12 }}>
        <div style={text.hint}>
          The room becomes the record of the response and goes into the Evidence Repository.
          {view.incident ? " Standing down does not close the incident: its notifications and post-incident review keep their own rules." : ""}
        </div>
        {down.length > 0 && <div style={{ ...text.body, color: theme.color.warningText }}>{down.map((p) => p.name).join(", ")} {down.length === 1 ? "is" : "are"} not recorded as recovered.</div>}
        {open.length > 0 && (
          <div>
            <div style={{ ...text.label, marginBottom: 4 }}>Open actions: carry into the corrective action register</div>
            <div style={{ display: "grid", gap: 4 }}>
              {open.map((a) => (
                <Check key={a.id} checked={carry.has(a.id)} label={`A-${a.number} ${a.title}${a.ownerName ? ` (${a.ownerName})` : ""}`}
                  onChange={(v) => setCarry((s) => { const n = new Set(s); if (v) n.add(a.id); else n.delete(a.id); return n; })} />
              ))}
            </div>
            {left.length > 0 && <div style={{ ...text.hint, color: theme.color.warningText, marginTop: 6 }}>Close {left.map((a) => `A-${a.number}`).join(", ")} on the board first, or carry {left.length === 1 ? "it" : "them"} forward.</div>}
          </div>
        )}
        <Field label="Why the room is standing down, and what remains"><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} maxLength={4000} required /></Field>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" style={{ ...btnPrimary, opacity: left.length || !note.trim() ? 0.6 : 1 }} disabled={left.length > 0 || !note.trim()}>Stand down</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function ReopenModal({ view, onClose, write }: { view: RoomView; onClose: () => void; write: Write }) {
  return (
    <ReasonModal
      title="Why is the room reopening?"
      onClose={onClose}
      onSave={async (reason) => { if (await write("reopen", () => api.post<RoomView>(`/response-rooms/${view.room.id}/reopen`, { reason }))) onClose(); }}
    />
  );
}

// ── Entry points from the registers ──────────────────────────────────────────

/**
 * "Open a response room" / "Go to the response room" for an incident or an exercise panel. Hidden
 * below the tier that carries rooms and for anyone who cannot write (the list answers 402 / 403).
 */
export function ResponseRoomLink({ incidentId, exerciseId, canOpen = true }: { incidentId?: string; exerciseId?: string; canOpen?: boolean }) {
  const { user } = useCurrentUser();
  const [rooms, setRooms] = useState<RoomListItem[] | null>(null);
  useEffect(() => {
    let live = true;
    api.get<RoomList>("/response-rooms").then((d) => { if (live) setRooms(d.rooms); }).catch(() => { if (live) setRooms(null); });
    return () => { live = false; };
  }, []);
  if (!rooms) return null;
  const existing = rooms.find((r) => (incidentId && r.incident?.id === incidentId) || (exerciseId && r.exercise?.id === exerciseId));
  if (existing) {
    return <PortalLink to={`/resilience/rooms/${existing.id}`} style={{ ...secondaryBtn, textDecoration: "none", display: "inline-flex", alignItems: "center" }}><Siren size={12} style={{ marginRight: 6 }} />{existing.status === "active" ? "Go to the response room" : "Response room record"}</PortalLink>;
  }
  if (!canOpen || !user?.permissions?.includes("resilience:write")) return null;
  const q = incidentId ? `incident=${incidentId}` : `exercise=${exerciseId}`;
  return <PortalLink to={`/resilience/rooms?${q}`} style={{ ...secondaryBtn, textDecoration: "none", display: "inline-flex", alignItems: "center" }}><Siren size={12} style={{ marginRight: 6 }} />{incidentId ? "Open a response room" : "Run it in a response room"}</PortalLink>;
}
